import { useEffect, useRef } from 'react';
import { useStore } from 'react-redux';
import { RootState } from '@/app/appStore';
import { DashProvider } from '@/app/context/dash-context';
import { Footer } from '@/pages/home/ui/footer';
import { InfoSidebar } from '@/pages/home/ui/info-sidebar';
import { Layout } from '@/pages/home/ui/layout';
import { NavHeader } from '@/pages/home/ui/nav-header';
import { Sidebar } from '@/pages/home/ui/sidebar';
import { Workspace } from '@/pages/home/ui/workspace';
import { setBgRectangle } from '@/entities/canvas';
import {
  initCollection,
  initFrames,
  sortCollections,
  sortImages,
} from '@/entities/collection';
import { useCollectionImagesQuery, useCollectionsQuery } from '@/shared/api';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';

const usePopulateCollection = () => {
  const { data } = useCollectionsQuery({
    page: 1,
    limit: 100_000,
    is_owner: true,
  });
  const dispatch = useAppDispatch();
  const isFirstTime = useRef(true);

  useEffect(() => {
    if (data && data.results.length > 0 && isFirstTime.current) {
      const pendingCollections = data.results.filter(
        (state) => state.status === 'pending'
      );

      if (pendingCollections.length === 0) return;

      const sortedDataByDesc = sortCollections(pendingCollections);
      const latestCollection = sortedDataByDesc[0];

      dispatch(initCollection(latestCollection));
      isFirstTime.current = false;
    }
  }, [data]);
};

const usePopulateFrames = () => {
  const store = useStore<RootState>();
  const collectionId = useAppSelector((state) => state.collection.collectionId);

  const { data } = useCollectionImagesQuery(
    {
      collection_id: collectionId!,
      page: 1,
      limit: 100_000,
    },
    {
      skip: !collectionId,
    }
  );

  const dispatch = useAppDispatch();
  const isFirstTime = useRef(true);

  useEffect(() => {
    if (
      data &&
      data.results.length > 0 &&
      isFirstTime.current &&
      collectionId
    ) {
      const sortedDataByAsc = sortImages(data.results);

      const { canvasWidth, canvasHeight } = store.getState().canvas.dimensions;
      const dimensions = {
        canvasWidth,
        canvasHeight,
        videoWidth: store.getState().collection.imageSize.width,
        videoHeight: store.getState().collection.imageSize.height,
      };

      const lastFrame = sortedDataByAsc[sortedDataByAsc.length - 1];
      const polygonFinderParameters = lastFrame.polygon_finder_parameters;

      dispatch(initFrames({ images: sortedDataByAsc, dimensions }));
      dispatch(setBgRectangle(polygonFinderParameters));
      isFirstTime.current = false;
    }
  }, [data]);
};

export function Home() {
  usePopulateCollection();
  usePopulateFrames();

  return (
    <DashProvider>
      <Layout leftSidebar1={<Sidebar />} leftSidebar2={<InfoSidebar />}>
        <main className="flex flex-col h-full">
          <NavHeader />
          <Workspace />
          <Footer />
        </main>
      </Layout>
    </DashProvider>
  );
}
